import { useEffect, useRef, useCallback } from "react";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";

export interface CookieConsentConfig {
  onAcceptAnalytics?: () => void;
  onRejectAnalytics?: () => void;
  currentLanguage?: "en" | "es" | "sv";
}

/**
 * Translations for the cookie consent banner.
 */
const translations = {
  en: {
    consentModal: {
      title: "Cookie Preferences",
      description:
        "We use cookies to enhance your experience and analyze site usage. You can customize your preferences below.",
      acceptAllBtn: "Accept All",
      acceptNecessaryBtn: "Necessary Only",
      showPreferencesBtn: "Manage Preferences",
    },
    preferencesModal: {
      title: "Cookie Preferences",
      acceptAllBtn: "Accept All",
      acceptNecessaryBtn: "Necessary Only",
      savePreferencesBtn: "Save Preferences",
      closeIconLabel: "Close",
      sections: [
        {
          title: "Cookie Usage",
          description:
            "We use cookies to ensure the basic functionality of the website and to enhance your online experience.",
        },
        {
          title: "Strictly Necessary Cookies",
          description:
            "These cookies are essential for the website to function properly. They cannot be disabled.",
          linkedCategory: "necessary",
        },
        {
          title: "Analytics Cookies",
          description:
            "These cookies help us understand how visitors interact with our website, helping us improve our services.",
          linkedCategory: "analytics",
        },
      ],
    },
  },
  es: {
    consentModal: {
      title: "Preferencias de Cookies",
      description:
        "Utilizamos cookies para mejorar su experiencia y analizar el uso del sitio. Puede personalizar sus preferencias a continuación.",
      acceptAllBtn: "Aceptar Todo",
      acceptNecessaryBtn: "Solo Necesarias",
      showPreferencesBtn: "Gestionar Preferencias",
    },
    preferencesModal: {
      title: "Preferencias de Cookies",
      acceptAllBtn: "Aceptar Todo",
      acceptNecessaryBtn: "Solo Necesarias",
      savePreferencesBtn: "Guardar Preferencias",
      closeIconLabel: "Cerrar",
      sections: [
        {
          title: "Uso de Cookies",
          description:
            "Utilizamos cookies para garantizar la funcionalidad básica del sitio web y mejorar su experiencia en línea.",
        },
        {
          title: "Cookies Estrictamente Necesarias",
          description:
            "Estas cookies son esenciales para que el sitio web funcione correctamente. No se pueden desactivar.",
          linkedCategory: "necessary",
        },
        {
          title: "Cookies de Análisis",
          description:
            "Estas cookies nos ayudan a comprender cómo los visitantes interactúan con nuestro sitio web.",
          linkedCategory: "analytics",
        },
      ],
    },
  },
  sv: {
    consentModal: {
      title: "Cookie-inställningar",
      description:
        "Vi använder cookies för att förbättra din upplevelse och analysera webbplatsanvändning. Du kan anpassa dina inställningar nedan.",
      acceptAllBtn: "Acceptera Alla",
      acceptNecessaryBtn: "Endast Nödvändiga",
      showPreferencesBtn: "Hantera Inställningar",
    },
    preferencesModal: {
      title: "Cookie-inställningar",
      acceptAllBtn: "Acceptera Alla",
      acceptNecessaryBtn: "Endast Nödvändiga",
      savePreferencesBtn: "Spara Inställningar",
      closeIconLabel: "Stäng",
      sections: [
        {
          title: "Cookie-användning",
          description:
            "Vi använder cookies för att säkerställa webbplatsens grundläggande funktionalitet och förbättra din online-upplevelse.",
        },
        {
          title: "Strikt Nödvändiga Cookies",
          description:
            "Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt. De kan inte inaktiveras.",
          linkedCategory: "necessary",
        },
        {
          title: "Analys-cookies",
          description:
            "Dessa cookies hjälper oss att förstå hur besökare interagerar med vår webbplats.",
          linkedCategory: "analytics",
        },
      ],
    },
  },
};

/**
 * React component for GDPR-compliant cookie consent.
 */
export function CookieConsentBanner({
  onAcceptAnalytics,
  onRejectAnalytics,
  currentLanguage = "en",
}: CookieConsentConfig) {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    CookieConsent.run({
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: "_gid" }, { name: /^_otel/ }],
          },
        },
      },

      guiOptions: {
        consentModal: {
          layout: "box",
          position: "bottom right",
          equalWeightButtons: false,
        },
        preferencesModal: {
          layout: "box",
        },
      },

      language: {
        default: currentLanguage,
        translations,
      },

      onConsent: () => {
        if (CookieConsent.acceptedCategory("analytics")) {
          onAcceptAnalytics?.();
        } else {
          onRejectAnalytics?.();
        }
      },

      onChange: ({ changedCategories }) => {
        if (changedCategories.includes("analytics")) {
          if (CookieConsent.acceptedCategory("analytics")) {
            onAcceptAnalytics?.();
          } else {
            onRejectAnalytics?.();
          }
        }
      },
    });
  }, [currentLanguage, onAcceptAnalytics, onRejectAnalytics]);

  // Update language when it changes
  useEffect(() => {
    if (initialized.current) {
      CookieConsent.setLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  return null;
}

/**
 * Hook to check if analytics consent has been given.
 */
export function useAnalyticsConsent() {
  const hasConsent = useCallback(() => {
    return CookieConsent.acceptedCategory("analytics");
  }, []);

  const showPreferences = useCallback(() => {
    CookieConsent.showPreferences();
  }, []);

  return {
    hasConsent,
    showPreferences,
  };
}
